// ============================================================================
// GST EXPECTED FILES — G-1 slice 3c (ADR-0025 §7, Revision 1 R1.1)
// ============================================================================
// The files each GSTIN's client sends every period, branch by branch, declared
// once a year: "PZR · billing-package sales register", "MAIN · Tally GST
// report". Each file feeds one or more streams (sales, HSN …), shown read-only
// under it: a single-stream kind has its stream from the start; a container
// kind (a GST report with several sheets) learns its streams — and every file
// learns which sheets feed them — from the first file received.
//
// Mandatory files gate a return period leaving "Collecting data": each must be
// received for the whole period, or waived for it with a reason.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import { gstExpectedSourceService } from 'api/services/gstExpectedSourceService';
import {
  GST_DOCUMENT_TYPES,
  GST_SOURCE_DOCUMENT_KINDS,
  type GstEngagement,
  type GstEngagementRegistration,
  type GstExpectedFile,
} from 'api/types/gstEngagement.types';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  /** Declaring and changing files follows the `edit` grant. */
  canEdit?: boolean;
  onClose: () => void;
}

interface FormState {
  id: string | null;
  registrationId: string;
  sourceRef: string;
  displayName: string;
  fileKind: string;
  isMandatory: boolean;
  notes: string;
  rowVersion: number;
}

const streamLabel = (v: string) => GST_DOCUMENT_TYPES.find((t) => t.value === v)?.label ?? v;
const kindLabel = (v: string) => GST_SOURCE_DOCUMENT_KINDS.find((k) => k.value === v)?.label ?? v;

/** Kinds that hold several streams — their streams are learned from the first file. */
const CONTAINER_KINDS = new Set(['ACCOUNTING_GST_REPORT', 'GSTR1_EXPORT', 'OTHER']);

const emptyForm = (registrationId: string): FormState => ({
  id: null, registrationId, sourceRef: '', displayName: '', fileKind: 'SALES_REGISTER', isMandatory: true, notes: '', rowVersion: 0,
});

const GstExpectedSourcesModal: React.FC<Props> = ({ isOpen, engagement, canEdit = false, onClose }) => {
  const [registrations, setRegistrations] = useState<GstEngagementRegistration[]>([]);
  const [files, setFiles] = useState<GstExpectedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<GstExpectedFile | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const formSnapshot = useRef('');
  const [discardOpen, setDiscardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!engagement) return;
    setLoading(true);
    try {
      const [detail, list] = await Promise.all([
        gstEngagementService.getById(engagement.id),
        gstExpectedSourceService.listFiles(engagement.id),
      ]);
      setRegistrations(detail.registrations ?? []);
      setFiles(list);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the expected files.');
    } finally {
      setLoading(false);
    }
  }, [engagement]);

  useEffect(() => { if (isOpen) { setForm(null); void load(); } }, [isOpen, load]);

  const byRegistration = useMemo(() => {
    const map = new Map<string, GstExpectedFile[]>();
    files.forEach((f) => map.set(f.registrationId, [...(map.get(f.registrationId) ?? []), f]));
    return map;
  }, [files]);

  const knownRefs = useMemo(() => Array.from(new Set(files.map((f) => f.sourceRef))).sort(), [files]);

  const kindOptions = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>();
    GST_SOURCE_DOCUMENT_KINDS.forEach((k) => groups.set(k.group, [...(groups.get(k.group) ?? []), { value: k.value, label: k.label }]));
    return Array.from(groups.entries());
  }, []);

  const openForm = (f: FormState) => { setForm(f); setFieldErrors({}); formSnapshot.current = JSON.stringify(f); };
  const formDirty = form !== null && JSON.stringify(form) !== formSnapshot.current;
  const closeForm = () => { if (formDirty) setDiscardOpen(true); else setForm(null); };

  const validate = (): boolean => {
    if (!form) return false;
    const e: Record<string, string> = {};
    const ref = form.sourceRef.trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (!ref) e.sourceRef = 'A short branch code — e.g. PZR, TWMLA, or MAIN for a single source.';
    else if (!/^[A-Z0-9_]{1,50}$/.test(ref)) e.sourceRef = 'Letters, digits and underscores only.';
    if (!form.displayName.trim()) e.displayName = 'Name the file the way the firm knows it — e.g. "Tally GST report".';
    else if (files.some((f) => f.id !== form.id && f.registrationId === form.registrationId && f.sourceRef === ref
      && f.displayName.toLowerCase() === form.displayName.trim().toLowerCase())) {
      e.displayName = `${ref} already has a file of this name.`;
    }
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!engagement || !form) return;
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const request = {
        registrationId: form.registrationId,
        sourceRef: form.sourceRef.trim(),
        displayName: form.displayName.trim(),
        fileKind: form.fileKind,
        isMandatory: form.isMandatory,
        notes: form.notes.trim() || null,
        rowVersion: form.rowVersion,
      };
      if (form.id) {
        await gstExpectedSourceService.updateFile(engagement.id, form.id, request);
        notificationService.toast.success('Expected file updated.');
      } else {
        const { message } = await gstExpectedSourceService.createFile(engagement.id, request);
        notificationService.toast.success(message);
      }
      setForm(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not save the expected file.', 8000);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!engagement || !deleting) return;
    setDeleteBusy(true);
    try {
      await gstExpectedSourceService.removeFile(engagement.id, deleting.id);
      notificationService.toast.success(`${deleting.displayName} is no longer expected.`);
      setDeleting(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not remove the expected file.');
    } finally {
      setDeleteBusy(false);
    }
  };

  if (!isOpen || !engagement) return null;

  const th: React.CSSProperties = { padding: '4px 8px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 600 };
  const td: React.CSSProperties = { padding: '5px 8px', borderTop: '1px solid var(--border-color)', verticalAlign: 'top' };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Expected files — {engagement.clientLegalName || engagement.clientName}</h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
            The files each GSTIN's client sends every period, branch by branch. Receiving a file as one of these parses it for the
            period in the same step. A return period cannot be marked prepared until every <strong>mandatory</strong> file is in,
            or waived for it with a reason. Which sheets feed which stream, and how each is mapped, are learned from the first file.
          </div>

          {loading && <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Loading…</div>}

          {!loading && registrations.map((reg) => {
            const list = byRegistration.get(reg.registrationId) ?? [];
            return (
              <div key={reg.registrationId} style={{ border: '1px solid var(--border-color)', borderRadius: 6, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: '6px 6px 0 0' }}>
                  <strong style={{ fontSize: '0.8125rem' }}>{reg.gstin}</strong>
                  {reg.tradeName && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{reg.tradeName}</span>}
                  <span style={{ flex: 1 }} />
                  {canEdit && (
                    <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }} onClick={() => openForm(emptyForm(reg.registrationId))}>
                      Add file
                    </button>
                  )}
                </div>
                {list.length === 0 ? (
                  <div style={{ padding: '8px 10px', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    Nothing declared — return periods for this GSTIN are not checked for completeness.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead>
                      <tr><th style={th}>Branch</th><th style={th}>File</th><th style={th}>Needed</th><th style={th}>What it feeds</th><th style={th} /></tr>
                    </thead>
                    <tbody>
                      {list.map((f) => (
                        <tr key={f.id}>
                          <td style={td}><strong>{f.sourceRef}</strong></td>
                          <td style={td}>
                            {f.displayName}
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{kindLabel(f.fileKind)}</div>
                            {Object.keys(f.partRoles ?? {}).length > 0 && (
                              <div style={{ fontSize: '0.7rem', marginTop: 2 }}>
                                {Object.entries(f.partRoles).map(([part, role]) => (
                                  <span key={part} className={role === 'PRIMARY' ? 'badge badge-info' : 'badge badge-inactive'} style={{ marginRight: 4 }}
                                    title={role === 'PRIMARY' ? 'The return part is built from this file' : 'Compared against the primary every period'}>
                                    {part} · {role === 'PRIMARY' ? 'primary' : 'cross-check'}
                                  </span>
                                ))}
                              </div>
                            )}
                            {f.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{f.notes}</div>}
                          </td>
                          <td style={td}>
                            <span className={f.isMandatory ? 'badge badge-info' : 'badge badge-inactive'}>{f.isMandatory ? 'Mandatory' : 'Optional'}</span>
                          </td>
                          <td style={{ ...td, fontSize: '0.75rem' }}>
                            {f.streams.length === 0 ? (
                              <span style={{ color: 'var(--text-secondary)' }}>learned from the first file received</span>
                            ) : f.streams.map((s) => (
                              <div key={s.id}>
                                <span style={{ color: 'var(--text-primary)' }}>{streamLabel(s.documentType)}</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  {' · '}{s.sheetPatterns.length ? `sheet ${s.sheetPatterns.join(', ')}` : 'sheet learned from the first file'}
                                  {' · '}{s.mappingProfileName ? `${s.mappingProfileName} v${s.mappingProfileVersion}` : 'profile learned when first mapped'}
                                </span>
                              </div>
                            ))}
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {canEdit && (
                              <>
                                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 8px', marginRight: 4 }} title="Edit"
                                  onClick={() => openForm({ id: f.id, registrationId: f.registrationId, sourceRef: f.sourceRef, displayName: f.displayName, fileKind: f.fileKind, isMandatory: f.isMandatory, notes: f.notes ?? '', rowVersion: f.rowVersion })}>
                                  <IconSystem.edit size={12} />
                                </button>
                                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 8px' }} title="Remove" onClick={() => setDeleting(f)}>
                                  <IconSystem.trash size={12} />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      {form && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            {saving && (
              <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving…</span>
              </div>
            )}
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{form.id ? 'Edit expected file' : 'Add expected file'} · {registrations.find((r) => r.registrationId === form.registrationId)?.gstin}</h2>
              <button className="modal-close" onClick={closeForm} title="Close"><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <div className="form-grid-2">
                <div className="form-group" data-field="sourceRef">
                  <label className="form-label">Branch code <span className="text-danger">*</span></label>
                  <input className={`form-input${fieldErrors.sourceRef ? ' form-field-error' : ''}`} list="gst-known-refs" value={form.sourceRef} disabled={!!form.id}
                    placeholder="PZR, TWMLA, MAIN" onChange={(e) => setForm({ ...form, sourceRef: e.target.value.toUpperCase() })} />
                  <datalist id="gst-known-refs">{knownRefs.map((r) => <option key={r} value={r} />)}</datalist>
                  {fieldErrors.sourceRef && <div className="form-error-message">{fieldErrors.sourceRef}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Kind of file <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.fileKind} disabled={!!form.id} onChange={(e) => setForm({ ...form, fileKind: e.target.value })}>
                    {kindOptions.map(([group, opts]) => (
                      <optgroup key={group} label={group}>
                        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {CONTAINER_KINDS.has(form.fileKind)
                      ? 'Holds several streams — which sheets feed which is learned from the first file.'
                      : `Feeds ${streamLabel(defaultStreamOf(form.fileKind)).toLowerCase()}; the sheet is learned from the first file.`}
                  </div>
                </div>
              </div>
              <div className="form-group" data-field="displayName">
                <label className="form-label">What the firm calls it <span className="text-danger">*</span></label>
                <input className={`form-input${fieldErrors.displayName ? ' form-field-error' : ''}`} value={form.displayName} maxLength={200}
                  placeholder="Tally GST report / PZR sales register (billing package)" onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
                {fieldErrors.displayName && <div className="form-error-message">{fieldErrors.displayName}</div>}
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={form.isMandatory} onChange={(e) => setForm({ ...form, isMandatory: e.target.checked })} />
                  Mandatory — the period cannot be marked prepared without it (or a waiver)
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">How to get it</label>
                <textarea className="form-textarea" rows={2} maxLength={1000} value={form.notes}
                  placeholder="e.g. Tally → Display → Statutory Reports → GST, export to Excel for the month"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={() => { if (validate()) setSaveConfirmOpen(true); }}>Save</button>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={saveConfirmOpen}
        title={form?.id ? 'Save changes?' : 'Add this file?'}
        message={form ? `${form.sourceRef.trim().toUpperCase()} — ${form.displayName.trim()} (${kindLabel(form.fileKind)}), ${form.isMandatory ? 'mandatory' : 'optional'}, expected every period of this engagement year.` : ''}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void save(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard changes?"
        message="You have unsaved changes to this file. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); setForm(null); }}
        onCancel={() => setDiscardOpen(false)}
      />
      <ConfirmDialog
        open={deleting !== null}
        title="Stop expecting this file?"
        message={deleting ? `${deleting.displayName} (${deleting.sourceRef}) will no longer be checked for any period this year, and what was learned about its sheets is dropped. Files already received are kept.` : ''}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteBusy}
        loadingMessage="Removing…"
        onConfirm={() => { void remove(); }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
};

/** The stream a single-stream file kind feeds (mirrors the API's GstDocumentTypes.DefaultFor). */
const defaultStreamOf = (kind: string): string => ({
  SALES_REGISTER: 'OUTWARD_SALES', SALES_RETURN: 'OUTWARD_SALES_RETURN', B2B: 'B2B_TABLE', B2CS: 'B2CS_TABLE', B2CL: 'B2CL_TABLE',
  HSN_SUMMARY: 'HSN_SUMMARY', CREDIT_NOTE_OUTWARD: 'OUTWARD_CREDIT_NOTE', DEBIT_NOTE_OUTWARD: 'OUTWARD_DEBIT_NOTE',
  PURCHASE_REGISTER: 'INWARD_PURCHASE', PURCHASE_RETURN: 'INWARD_PURCHASE_RETURN', CREDIT_NOTE_INWARD: 'INWARD_CREDIT_NOTE',
  DEBIT_NOTE_INWARD: 'INWARD_DEBIT_NOTE',
} as Record<string, string>)[kind] ?? '';

export default GstExpectedSourcesModal;
